import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdCancel, MdRefresh, MdCalendarToday, MdAccessTime,
  MdLocalHospital, MdChevronRight,
} from 'react-icons/md';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import usePoll from '../hooks/usePoll';
import {
  bookingId, categorizeBookings, isEmergencyBooking, isSosBooking,
  fmtTime, fmtDate, fmtDateBlock, statusMeta, shouldShowCheckInOtp,
  displayDoctorName, displayHospitalName, isDoctorBooking,
} from '../utils/bookingHelpers';
import CheckInOtp from '../components/CheckInOtp';

const TABS = [
  { id: 'upcoming',  label: 'Upcoming' },
  { id: 'emergency', label: '🚨 Emergency' },
  { id: 'past',      label: 'Past' },
];

function copyOtp(otp, showToast) {
  if (!otp) return;
  navigator.clipboard?.writeText(otp)
    .then(() => showToast('OTP copied!', 'success'))
    .catch(() => {});
}

function openBooking(navigate, b) {
  const id = bookingId(b);
  if (!id) return;
  if (isSosBooking(b) && b.alertId) {
    navigate(`/emergency?alertId=${encodeURIComponent(b.alertId)}&bookingId=${encodeURIComponent(id)}`, { state: { booking: b } });
    return;
  }
  if (isEmergencyBooking(b)) {
    navigate(`/bookings/${encodeURIComponent(id)}?emergency=1`, { state: { booking: b } });
    return;
  }
  navigate(`/bookings/${encodeURIComponent(id)}`, { state: { booking: b } });
}

// ── Emergency card (matches app red style) ──────────────────────────────────
function EmergencyCard({ b, onOpen, onCancel, cancelling }) {
  const id = bookingId(b);
  const st = (b.status || 'pending').toLowerCase();
  const statusLabel = st === 'confirmed' ? 'Hospital Notified ✓'
    : st === 'checked_in' ? 'Checked In ✓' : 'Sent';

  return (
    <div className="booking-card-emergency" onClick={() => onOpen(b)} role="button" tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onOpen(b)}>
      <div className="booking-emergency-header">
        <span>🚨 EMERGENCY OP</span>
        <span className="booking-emergency-badge">{statusLabel}</span>
      </div>
      <div className="booking-emergency-body">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
          <span className="badge" style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}>
            {displayDoctorName(b)}
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{displayHospitalName(b)}</span>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
          <span><MdCalendarToday size={12} style={{ verticalAlign: -2 }} /> {fmtDate(b.date)}</span>
          {b.slot && b.slot !== 'EMERGENCY' && b.slot !== 'SOS' && (
            <span><MdAccessTime size={12} style={{ verticalAlign: -2 }} /> {fmtTime(b.slot)}</span>
          )}
          <span className="badge badge-amber" style={{ fontSize: 10 }}>IMMEDIATE</span>
        </div>
        <div className="booking-ref-inline">
          <div className="booking-ref-label">BOOKING REFERENCE</div>
          <div className="booking-ref-id-sm">{id}</div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.4 }}>
          Show this ID at hospital reception
        </div>
      </div>
      <div className="booking-card-footer">
        <span>View Details</span>
        <MdChevronRight size={16} />
      </div>
      {st !== 'cancelled' && (
        <button type="button" className="booking-cancel-float"
          onClick={e => { e.stopPropagation(); onCancel(b); }}
          disabled={cancelling === id} title="Cancel">
          {cancelling === id ? '…' : <MdCancel />}
        </button>
      )}
    </div>
  );
}

// ── OP / Doctor card (clickable, OTP preview like app) ──────────────────────
function OpCard({ b, showCancel, onOpen, onCancel, cancelling }) {
  const id = bookingId(b);
  const st = (b.status || 'pending').toLowerCase();
  const sm = statusMeta(st);
  const isDoc = isDoctorBooking(b);
  const isCancelled = st === 'cancelled';
  const block = fmtDateBlock(b.date);

  return (
    <div className={`booking-card booking-status-${st} booking-card-clickable`}
      onClick={() => onOpen(b)} role="button" tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onOpen(b)}>

      <div className="booking-date-block" style={{ borderColor: `${sm.color}55` }}>
        <div className="booking-date-day" style={{ color: sm.color }}>{block.day}</div>
        <div className="booking-date-month">{block.month}</div>
        <div className="booking-date-year">{block.year}</div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 2 }}>
          {displayDoctorName(b)}
        </div>
        {isDoc && b.specialization && (
          <div style={{ fontSize: 12, color: 'var(--secondary)', marginBottom: 4 }}>{b.specialization}</div>
        )}
        <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 3, marginBottom: 6 }}>
          <MdLocalHospital size={12} /> {displayHospitalName(b)}
        </div>
        {b.slot && (
          <span className="badge badge-blue" style={{ fontSize: 11 }}>
            <MdAccessTime size={11} style={{ verticalAlign: -1, marginRight: 3 }} />
            {fmtTime(b.slot)}
          </span>
        )}
        {b.isRescheduled && !isCancelled && (
          <div style={{ fontSize: 10, color: 'var(--warning)', marginTop: 6 }}>↔ Rescheduled</div>
        )}
      </div>

      {showCancel && !isCancelled && (
        <button type="button" className="btn btn-sm booking-action-btn"
          onClick={e => { e.stopPropagation(); onCancel(b); }}
          disabled={cancelling === id}
          title="Cancel appointment">
          {cancelling === id ? '…' : <MdCancel />}
        </button>
      )}
    </div>
  );
}

function OpCardExtras({ b, showToast, onOpen }) {
  const st = (b.status || 'pending').toLowerCase();
  const sm = statusMeta(st);
  const showOtp = shouldShowCheckInOtp(b);

  return (
    <>
      {showOtp && (
        <CheckInOtp
          otp={b.otp || b.bookingOtp || b.checkInOtp}
          variant="dark"
          onCopy={(code) => copyOtp(code, showToast)}
        />
      )}
      <div className="booking-card-footer-row" onClick={() => onOpen(b)}>
        <span className="badge" style={{ background: sm.bg, color: sm.color }}>{sm.label}</span>
        <span className="view-details-link">View details <MdChevronRight size={14} /></span>
      </div>
    </>
  );
}

function OpCardWrapper({ b, showCancel, onOpen, onCancel, cancelling, showToast }) {
  return (
    <div className="booking-card-wrap">
      <OpCard b={b} showCancel={showCancel} onOpen={onOpen} onCancel={onCancel} cancelling={cancelling} />
      <OpCardExtras b={b} showToast={showToast} onOpen={onOpen} />
    </div>
  );
}

function EmptyTab({ emoji, title, subtitle, btnLabel, onBtn }) {
  return (
    <div className="empty" style={{ padding: '40px 20px' }}>
      <div className="empty-icon">{emoji}</div>
      <div className="empty-title">{title}</div>
      <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>{subtitle}</div>
      <button type="button" className="btn btn-primary" onClick={onBtn}>{btnLabel}</button>
    </div>
  );
}

export default function MyBookingsPage() {
  const navigate = useNavigate();
  const { user, showToast } = useAuth();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('upcoming');
  const [cancelling, setCancelling] = useState('');

  const loadBookings = useCallback(async (silent = false) => {
    if (!user) { setLoading(false); return; }
    if (!silent) setLoading(true);

    try {
      const r = await api.getMyBookingsAll(user);
      const items = r.data || [];
      setBookings(items.sort((a, b) => {
        const da = (a.createdAt || a.bookingDate || a.date || '');
        const db = (b.createdAt || b.bookingDate || b.date || '');
        return db > da ? 1 : -1;
      }));
    } finally {
      setLoading(false);
    }
  }, [user]);

  usePoll(useCallback(() => loadBookings(true), [loadBookings]), 2500);

  const cancelBooking = async (b) => {
    if (!window.confirm('Cancel this appointment?')) return;
    const id = bookingId(b);
    setCancelling(id);
    const r = await api.cancelBooking(id);
    setCancelling('');
    if (r.error) { showToast('Failed: ' + r.error, 'error'); return; }
    setBookings(prev => prev.map(x => bookingId(x) === id ? { ...x, status: 'cancelled' } : x));
    showToast('Appointment cancelled', 'success');
  };

  if (!user) {
    return (
      <div className="page empty">
        <div className="empty-icon">🔐</div>
        <div className="empty-title">Login to view bookings</div>
        <button type="button" className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/login')}>
          Login / Sign Up
        </button>
      </div>
    );
  }

  const { emergencies, upcoming, past } = categorizeBookings(bookings);
  const tabCounts = { upcoming: upcoming.length, emergency: emergencies.length, past: past.length };

  const listForTab = tab === 'upcoming' ? upcoming : tab === 'emergency' ? emergencies : past;

  return (
    <div className="fade-in">
      <div className="page">

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div className="section-title">📅 My Appointments</div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => loadBookings()} disabled={loading}>
            <MdRefresh /> Refresh
          </button>
        </div>
        <div className="section-sub">{bookings.length} total · Same layout as HCEN app</div>

        {/* App-style tabs: Upcoming | Emergency | Past */}
        <div className="bookings-tabs" style={{ marginBottom: 20 }}>
          {TABS.map(t => (
            <button key={t.id} type="button"
              className={`bookings-tab${tab === t.id ? ' active' : ''}${t.id === 'emergency' ? ' emergency' : ''}`}
              onClick={() => setTab(t.id)}>
              {t.label}
              {tabCounts[t.id] > 0 && (
                <span className="bookings-tab-count">{tabCounts[t.id]}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? <div className="spinner" /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {tab === 'emergency' && listForTab.map(b => (
              <EmergencyCard key={bookingId(b)} b={b}
                onOpen={b2 => openBooking(navigate, b2)}
                onCancel={cancelBooking}
                cancelling={cancelling} />
            ))}

            {tab !== 'emergency' && listForTab.map(b => (
              <OpCardWrapper key={bookingId(b)} b={b}
                showCancel={tab === 'upcoming'}
                onOpen={b2 => openBooking(navigate, b2)}
                onCancel={cancelBooking}
                cancelling={cancelling}
                showToast={showToast} />
            ))}

            {listForTab.length === 0 && !loading && (
              tab === 'upcoming' ? (
                <EmptyTab emoji="📅" title="No Upcoming Appointments"
                  subtitle="You have no upcoming OP bookings. Book one now!"
                  btnLabel="Book Appointment" onBtn={() => navigate('/hospitals')} />
              ) : tab === 'emergency' ? (
                <EmptyTab emoji="🚑" title="No Active Emergencies"
                  subtitle="Your emergency OP submissions will appear here."
                  btnLabel="Book Appointment" onBtn={() => navigate('/hospitals')} />
              ) : (
                <EmptyTab emoji="🗓️" title="No Past Appointments"
                  subtitle="Your completed appointments will appear here."
                  btnLabel="Book New Appointment" onBtn={() => navigate('/hospitals')} />
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
